/**
 * Sync Logs React Query Hook
 *
 * Provides data fetching for sync log history per bank connection.
 */

import { useQuery } from '@tanstack/react-query';
import { syncLogService, type SyncLogParams } from '../services/sync-log-service';

// =============================================================================
// Query Key Factory
// =============================================================================

export const syncLogKeys = {
  all: ['sync-logs'] as const,
  lists: () => [...syncLogKeys.all, 'list'] as const,
  list: (params?: SyncLogParams) => [...syncLogKeys.lists(), params] as const,
};

// =============================================================================
// Query Hook
// =============================================================================

/**
 * Hook to fetch sync logs for a bank connection.
 *
 * @param connectionId - Bank connection UUID to fetch logs for
 * @param options - Optional overrides (page, page_size)
 */
export function useSyncLogs(
  connectionId: string | undefined,
  options?: { page?: number; page_size?: number }
) {
  const params: SyncLogParams = {
    connection_id: connectionId,
    page_size: options?.page_size ?? 20,
    ...(options?.page ? { page: options.page } : {}),
  };

  return useQuery({
    queryKey: syncLogKeys.list(params),
    queryFn: () => syncLogService.list(params),
    enabled: !!connectionId,
  });
}
